/**
 * ROUTE_CAPS (PERM-ENFORCE-01c, review 2026-11-02 §6) — the ONE map from a page
 * to the capabilities that open it. App.tsx wraps each route in `RequireCap`
 * with the entry here; the Sidebar hides a rail item whose entry the login does
 * not hold. One map, so the rail and the router can never disagree (the
 * CRUMB-NAV-01 rule applied to permissions).
 *
 * Semantics: ANY of the listed keys opens the page; the admin wildcard always
 * does; a page with no entry needs only the shell (`office.access`).
 * "view" keys open list + record pages; the writes inside them are gated by
 * their own keys (`useCan`), never by the route.
 */
import { NAV as SETTINGS_NAV } from "@/features/settings/settingsIndex";

/** Any capability that opens at least one Settings section opens /settings. */
export const SETTINGS_ANY: string[] = [...new Set(SETTINGS_NAV.map((e) => e.cap))];

export const ROUTE_CAPS: Record<string, string[]> = {
  // The day
  "/today": ["office.access"],
  "/inbox": ["office.access"],
  "/board": ["board.view"],
  "/board/map": ["board.view"],
  "/dispatch/:dispatch": ["board.view", "board.view_self"],
  "/customers": ["customers.view"],
  "/customers/:custno": ["customers.view"],
  "/agreements": ["agreements.view"],
  "/agreements/planner": ["agreements.view"],
  "/agreements/route": ["agreements.view"],
  // Sell & build
  "/quotes": ["quotes.view"],
  "/quotes/:id": ["quotes.view"],
  "/jobs": ["jobs.view"],
  "/jobs/:jobId": ["jobs.view"],
  "/jobs/:jobId/invoice": ["jobs.view", "accounting.view"],
  "/jobs/:jobId/install-order": ["jobs.view"],
  "/jobs/:jobId/rebate-test": ["jobs.view"],
  "/jobs/:jobId/pick-sheet": ["jobs.view", "warehouse.view"],
  "/warehouse": ["warehouse.view"],
  "/invoices": ["accounting.view"],
  "/invoice/:invoice": ["accounting.view"],
  "/invoices/:invoiceId": ["accounting.view", "jobs.view"],
  "/invoices/esc/:escNo": ["accounting.view"],
  "/purchase-orders": ["po.view"],
  "/vendors": ["po.view", "vendors.edit"],
  "/parts/print": ["warehouse.view", "reports.parts-day", "board.view"],
  // Company
  "/team": ["team.view"],
  "/team/tv": ["team.view"],
  "/team/oncall/print": ["team.view"],
  "/items": ["pricebook.view"],
  "/marketing": ["marketing.view", "marketing.send"],
  "/email-builder": ["marketing.send"],
  "/reports": ["reports.view"],
  "/automations": ["automations.manage", "settings.messages"],
  "/settings": SETTINGS_ANY,
  "/editor-spike": ["settings.system"],
  // Field
  "/m": ["mobile.access", "mobile.manager"],
};

/** The keys that open a route, or [] when only the shell is required. */
export function routeCaps(path: string): string[] {
  return ROUTE_CAPS[path] ?? [];
}
